import _ from 'underscore';
import * as putil from '@bingads-webui-universal/primitive-utilities';
import { applyPartialConversionColumnName } from '@bingads-webui-campaign/column-conversions';
import { getTimeGridColumnConfig } from './time-grid-column-config';
import { Constants } from '../constants';
import { columnConfigToColumnChooserConfig, isConversionsColumnAllowed } from '../column-config-to-chooser-config';
import { updateSovColumns } from '../shared/util';

export function getTimeGridConfig({
  i18n,
  jsDataCampaign,
  edm,
  globalConfig,
  timeSchema,
  getCurrentLevel,
  bingadsGridColumn,
  gridType,
  isForVNext,
}) {
  const permissions = globalConfig.get('Permissions') || {};
  const {
    IsViewThroughConversionEnabled,
    IsAvgPositionDeprecationPhase1Enabled,
    IsAvgPositionDeprecationPhase2Enabled,
    IsManualCpmEnabled,
    // IsViewThroughConversionsRevenueEnabled,
  } = permissions;
  // (youngim) MT bug needs to be fixed for dimensinos
  const IsViewThroughConversionsRevenueEnabled = false;

  const updateEntitySchemaForRemovedPerformanceMetrics = _.identity;
  const updateEntitySchemaForSOV = _.identity;


  const timeColumnConfig = getTimeGridColumnConfig({
    i18n,
    bingadsGridColumn,
    globalConfig,
    getCurrentLevel,
  });

  return {
    getColumns: () => timeColumnConfig.getGridSpecificColumns(gridType)
      .concat(timeColumnConfig.getLevelOfDetailColumns())
      .concat(timeColumnConfig.getPerfColumns())
      .concat(timeColumnConfig.getConversionColumns())
      .concat(timeColumnConfig.getCompetitiveColumns()),
    getColumnChooserConfiguration: (logger) => {
      const level = getCurrentLevel(logger);
      const lockedColumns = [];

      if (logger) {
        logger.trace(`Current level: ${level}`, 'Dimension/load');
      }

      switch (gridType) {
        case Constants.TimeHour:
          lockedColumns.push('HourOfDay');
          break;
        case Constants.TimeDay:
          lockedColumns.push('DayOfWeek');
          break;
        case Constants.TimeDate:
          lockedColumns.push('GregorianDate');
          break;
        case Constants.TimeWeek:
          lockedColumns.push('Week');
          break;
        case Constants.TimeMonth:
          lockedColumns.push('Month');
          break;
        case Constants.TimeQuarter:
          lockedColumns.push('Quarter');
          break;
        case Constants.TimeYear:
          lockedColumns.push('Year');
          break;
        default:
          break;
      }
      lockedColumns.push('Impressions');

      const columnChooserConfig = {
        categories: [{
          name: i18n.getString('LevelOfDetail'),
          options: columnConfigToColumnChooserConfig(
            [
              ...timeColumnConfig.getGridSpecificColumns(gridType),
              ...timeColumnConfig.getLevelOfDetailColumns(level),
            ],
            lockedColumns
          ),
        }, {
          name: i18n.getString('Performance'),
          options: columnConfigToColumnChooserConfig(
            timeColumnConfig.getPerfColumns(),
            lockedColumns
          ),
        }, {
          name: i18n.getString('Conversions'),
          options: columnConfigToColumnChooserConfig(
            _.filter(
              timeColumnConfig.getConversionColumns(),
              ({ name }) => isConversionsColumnAllowed({
                name,
                IsViewThroughConversionEnabled,
                IsViewThroughConversionsRevenueEnabled,
              })
            ),
            lockedColumns
          ),
        }],
        selected: lockedColumns.concat(applyPartialConversionColumnName(permissions, _.compact([
          'Clicks',
          'CTR',
          'AverageCPC',
          !IsAvgPositionDeprecationPhase1Enabled && !IsAvgPositionDeprecationPhase2Enabled && 'AveragePosition',
          'ConversionRate',
          'RevenueConversion',
          'CPA',
          'Conversions',
          IsAvgPositionDeprecationPhase1Enabled && 'TopImpressionRatePercent',
          IsAvgPositionDeprecationPhase1Enabled && 'AbsoluteTopImpressionRatePercent',
          IsManualCpmEnabled && 'AverageCPM',
        ]))),
        mandatory: lockedColumns,
        locked: lockedColumns,
        isForVNext,
        patchQuery: true,
      };

      if (gridType >= Constants.TimeHour && gridType <= Constants.TimeYear) {
        columnChooserConfig.categories.push({
          name: i18n.getString('Competitive'),
          options: columnConfigToColumnChooserConfig(
            timeColumnConfig.getCompetitiveColumns(),
            lockedColumns
          ),
        });
      }

      if (logger) {
        logger.trace(`Current selected columns: ${columnChooserConfig.selected}`, 'Dimension/load');
      }

      if (isForVNext) {
        columnChooserConfig.customToolbarIcon = 'ba-TripleColumnSolid';
      }

      return columnChooserConfig;
    },

    getEntity: () => jsDataCampaign.Time,

    getEntitySet: () => _.result(edm.currentAccount, 'Time'),

    getSchema: () => {
      const level = getCurrentLevel();

      const columns = ['HourOfDay', 'DayOfWeek', 'GregorianDate', 'Week',
        'Month', 'Quarter', 'Year', 'CampaignName', 'AdGroupName'];

      timeSchema.properties.PerformanceMetrics = // eslint-disable-line no-param-reassign
        putil.cloneDeep(timeSchema.properties.PerformanceMetrics);
      const performanceMetricsProperty = timeSchema.properties.PerformanceMetrics;
      const allConvMetrics = applyPartialConversionColumnName(permissions, [
        'AllConversionCPA',
        'AllConversions',
        'AllConversionRate',
        'AllConversionAdvertiserReportedRevenue',
        'AllConversionRevenueOnAdSpend',
        'ViewThroughConversions',
        'ViewThroughConversionsRevenue',
        'ViewThroughConversionsCPA',
        'ViewThroughConversionsReturnOnAdSpend',
        'ViewThroughConversionsRate',
      ]);

      const timeGridSovColumnNames = {
        AuctionLostToRankPercent: 'AuctionLostToRankAggPercent',
        AbsoluteTopImpressionRate: 'AbsoluteTopImpressionRatePercent',
        TopImpressionRate: 'TopImpressionRatePercent',
        TopISLostToRankPercent: 'TopImpressionShareLostToRankPercent',
        TopISLostToBudgetPercent: 'TopImpressionShareLostToBudgetPercent',
        AbsoluteTopISLostToRankPercent: 'AbsoluteTopImpressionShareLostToRankPercent',
        AbsoluteTopISLostToBudgetPercent: 'AbsoluteTopImpressionShareLostToBudgetPercent',
      };

      _.each(allConvMetrics, (allConvMetric) => {
        if (performanceMetricsProperty &&
            putil.get(performanceMetricsProperty.schema.properties, allConvMetric)) {
          timeSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
            .schema.properties[allConvMetric].filterable = true;
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversions')) {
            timeSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsRevenue')) {
            timeSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsCPA')) {
            timeSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsReturnOnAdSpend')) {
            timeSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
          if (allConvMetric === applyPartialConversionColumnName(permissions, 'ViewThroughConversionsRate')) {
            timeSchema.properties.PerformanceMetrics // eslint-disable-line no-param-reassign
              .schema.properties[allConvMetric].filterable =
                IsViewThroughConversionsRevenueEnabled;
          }
        }
      });

      // remove AverageCPM since we don't support this in V1
      delete timeSchema.properties.PerformanceMetrics.schema.properties.AverageCPM; // eslint-disable-line no-param-reassign, max-len
      delete timeSchema.properties.PerformanceMetrics.schema.properties.ClickSharePercent; // eslint-disable-line no-param-reassign, max-len
      delete timeSchema.properties.PerformanceMetrics.schema.properties.ExactMatchImpressionSharePercent; // eslint-disable-line no-param-reassign, max-len

      _.each(Object.keys(timeSchema.properties), (propertyKey) => {
        if (columns.indexOf(propertyKey) > -1) {
          delete timeSchema.properties[propertyKey]; // eslint-disable-line no-param-reassign
        }
      });

      updateSovColumns(timeSchema, timeGridSovColumnNames);

      switch (gridType) {
        case Constants.TimeHour:
          timeSchema.properties.HourOfDay = { // eslint-disable-line no-param-reassign
            type: 'integer',
            filterable: false,
          };
          break;
        case Constants.TimeDay:
          timeSchema.properties.DayOfWeek = { // eslint-disable-line no-param-reassign
            type: 'integer',
            filterable: false,
          };
          break;
        case Constants.TimeDate:
          timeSchema.properties.GregorianDate = { // eslint-disable-line no-param-reassign
            type: 'integer',
            filterable: false,
          };
          break;
        case Constants.TimeWeek:
          timeSchema.properties.Week = { // eslint-disable-line no-param-reassign
            type: 'integer',
            filterable: false,
          };
          break;
        case Constants.TimeMonth:
          timeSchema.properties.Month = { // eslint-disable-line no-param-reassign
            type: 'integer',
            filterable: false,
          };
          break;
        case Constants.TimeQuarter:
          timeSchema.properties.Quarter = { // eslint-disable-line no-param-reassign
            type: 'integer',
            filterable: false,
          };
          break;
        case Constants.TimeYear:
          timeSchema.properties.Year = { // eslint-disable-line no-param-reassign
            type: 'integer',
            filterable: false,
          };
          break;
        default:
          break;
      }

      if (level === Constants.AccountLevel) {
        timeSchema.properties.CampaignName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
        timeSchema.properties.AdGroupName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
      } else if (level === Constants.CampaignLevel) {
        timeSchema.properties.AdGroupName = { // eslint-disable-line no-param-reassign
          type: 'string',
          filterable: true,
          nullable: true,
        };
      }

      const schema = _.compose(
        updateEntitySchemaForSOV,
        updateEntitySchemaForRemovedPerformanceMetrics
      )(timeSchema);

      return schema;
    },
    getReportName: () => {
      let reportType = null;

      switch (gridType) {
        case Constants.TimeHour:
          reportType = 'TimeHourOfDay';
          break;
        case Constants.TimeDay:
          reportType = 'TimeDayOfWeek';
          break;
        case Constants.TimeDate:
          reportType = 'TimeDate';
          break;
        case Constants.TimeWeek:
          reportType = 'TimeWeek';
          break;
        case Constants.TimeMonth:
          reportType = 'TimeMonth';
          break;
        case Constants.TimeQuarter:
          reportType = 'TimeQuarter';
          break;
        case Constants.TimeYear:
          reportType = 'TimeYear';
          break;
        default:
          break;
      }
      return reportType;
    },
    getReportSchema() {
      return _.defaults({
        name: this.getReportName(gridType),
      }, this.getSchema(gridType));
    },
    modifyFilter: filters => filters,
    getRewriteMap: () => ({
      HourOfDay: i18n.getString('ColumnName_HourOfDay'),
      DayOfWeek: i18n.getString('ColumnName_DayOfWeek'),
      GregorianDate: i18n.getString('ColumnName_Date'),
      Week: i18n.getString('ColumnName_Week'),
      Month: i18n.getString('ColumnName_Month'),
      Quarter: i18n.getString('ColumnName_Quarter'),
      Year: i18n.getString('ColumnName_Year'),
      AdGroupName: i18n.getString('FilterLocalization_DataAttribute_AdGroupName'),
      CampaignName: i18n.getString('FilterLocalization_DataAttribute_CampaignName'),
      AuctionWonPercent: i18n.getString('ColumnName_ImpShare'),
      AuctionLostToBudgetPercent: i18n.getString('ColumnName_ImpShareLostToBudget'),
      AuctionLostToRankPercent: i18n.getString('ColumnName_ImpShareLostToRank'),
      AuctionLostToLandingPercent: i18n.getString('ColumnName_ImpShareLostToRelevance'),
      AuctionLostToAdQualityPercent: i18n.getString('ColumnName_ImpShareLostToExpectedCTR'),
      AuctionLostToBidPercent: i18n.getString('ColumnName_ImpShareLostToBid'),
      TopImpressionSharePercent: i18n.getString('ColumnName_TopImpressionSharePercent'),
      TopImpressionShareLostToBudgetPercent: i18n.getString('ColumnName_TopImpressionShareLostToBudget'),
      TopImpressionShareLostToRankPercent: i18n.getString('ColumnName_TopImpressionShareLostToRank'),
      AbsoluteTopImpressionSharePercent: i18n.getString('ColumnName_AbsoluteTopImpressionSharePercent'),
      AbsoluteTopImpressionShareLostToBudgetPercent: i18n.getString('ColumnName_AbsoluteTopImpressionShareLostToBudget'),
      AbsoluteTopImpressionShareLostToRankPercent: i18n.getString('ColumnName_AbsoluteTopImpressionShareLostToRank'),
      TopImpressionRatePercent: i18n.getString('ColumnName_TopImpressionRate'),
      AbsoluteTopImpressionRatePercent: i18n.getString('ColumnName_AbsoluteTopImpressionRate'),
    }),
  };
}

